/**
 * Shared form-construction helpers for the auth views (#117).
 *
 * Every input must satisfy the AC checklist:
 *   - `<label for>` pairs with the input's `id`
 *   - error messages live in `aria-describedby` (and `role="alert"` so
 *     screen readers announce them on submit failure)
 *   - focus moves to the first error on submit (`focusFirstError`)
 *   - keyboard-only navigable (no JS-only click handlers without an
 *     equivalent `onSubmit` on the form)
 *   - i18n: every visible string comes from `t()` at the call site
 *
 * Errors are stored on a single DOM node per form keyed by `data-error-
 * for=<input-id>` — the input's `aria-describedby` points at it. The
 * `setFieldError` helper updates the text + toggles `aria-invalid` on
 * the input.
 */

/** Focus the first input within `form` whose `aria-invalid` is "true". */
export function focusFirstError(form: HTMLFormElement): void {
  const invalid = form.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (invalid) {
    invalid.focus();
    if (invalid instanceof HTMLInputElement) {
      invalid.select();
    }
  }
}

/**
 * Set an inline error for the given input. The input is marked
 * `aria-invalid="true"`, and the paired `[data-error-for=<id>]` node
 * (which the input's `aria-describedby` references) is set to the
 * given message. Passing `null` clears the error.
 */
export function setFieldError(form: HTMLFormElement, inputId: string, message: string | null): void {
  const input = form.querySelector<HTMLElement>(`#${CSS.escape(inputId)}`);
  const errorNode = form.querySelector<HTMLElement>(`[data-error-for="${CSS.escape(inputId)}"]`);
  if (input) {
    if (message === null) {
      input.removeAttribute('aria-invalid');
    } else {
      input.setAttribute('aria-invalid', 'true');
    }
  }
  if (errorNode) {
    errorNode.textContent = message ?? '';
  }
}

/** Clear every inline error on a form (used at the start of submit). */
export function clearFormErrors(form: HTMLFormElement): void {
  for (const input of form.querySelectorAll<HTMLElement>('[aria-invalid]')) {
    input.removeAttribute('aria-invalid');
  }
  for (const node of form.querySelectorAll<HTMLElement>('[data-error-for]')) {
    node.textContent = '';
  }
  // Clear the form-level error region too.
  const formError = form.querySelector<HTMLElement>('[data-form-error]');
  if (formError) formError.textContent = '';
}

/** Display a form-level (non-field-specific) error message. */
export function setFormError(form: HTMLFormElement, message: string): void {
  const node = form.querySelector<HTMLElement>('[data-form-error]');
  if (node) node.textContent = message;
}

/** Lock/unlock the submit button while a request is in flight. */
export function setSubmitting(form: HTMLFormElement, submitting: boolean): void {
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (submit) submit.disabled = submitting;
}

export interface AuthSuccessSpec {
  /** Heading text (default sans). */
  heading: string;
  /** Body copy. */
  body: string;
  /** `data-testid` on the body paragraph (tests assert on this). */
  bodyTestid: string;
  /** Optional success/state variant — `success` paints the check-mark glyph. */
  variant?: 'success' | 'error';
  /** Continue / back link href. */
  linkHref: string;
  /** Continue / back link text. */
  linkText: string;
  /** `data-testid` on the link. */
  linkTestid: string;
}

/**
 * Replace an `.auth-card`'s contents with a composed success/result
 * panel (#150). The auth views previously hand-rolled bare `<h1>`/`<p>`/
 * `<a>` nodes with no classes, so the success states rendered unstyled.
 * This helper builds the same DOM the live views use — a head block, a
 * status glyph, the body copy, and a styled action link — so the
 * post-submit panels match the design-system auth-card treatment.
 *
 * Kept imperative (not an llui view) because the auth views flip to
 * success by mutating the mounted card directly rather than driving the
 * TEA loop — the existing contract these views were built around.
 */
export function renderAuthSuccess(card: HTMLElement, spec: AuthSuccessSpec): void {
  const variant = spec.variant ?? 'success';
  card.innerHTML = '';
  card.classList.add('auth-card--result');

  const head = document.createElement('div');
  head.className = 'auth-card-head';

  const glyph = document.createElement('span');
  glyph.className = `auth-result-glyph auth-result-glyph--${variant}`;
  glyph.setAttribute('aria-hidden', 'true');
  glyph.textContent = variant === 'success' ? '✓' : '!';
  head.appendChild(glyph);

  const heading = document.createElement('h1');
  heading.textContent = spec.heading;
  head.appendChild(heading);
  card.appendChild(head);

  const body = document.createElement('p');
  body.className = 'auth-card-subtitle';
  body.setAttribute('data-testid', spec.bodyTestid);
  body.textContent = spec.body;
  card.appendChild(body);

  const linkRow = document.createElement('div');
  linkRow.className = 'auth-form-links auth-form-links--center';
  const link = document.createElement('a');
  link.href = spec.linkHref;
  link.setAttribute('data-testid', spec.linkTestid);
  link.textContent = spec.linkText;
  linkRow.appendChild(link);
  card.appendChild(linkRow);
}
